import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import { platformUsersKeys } from "../../api/queryKeys";
import LoadingIcon from "../../components/LoadingIcon";
import RedAsterisk from "../../components/RedAsterisk";
import { Toast } from "../../components/Toast";
import AddNewTenantForm from "./AddNewTenantForm";

const AddNewTenantModal = ({
  open,
  onClose,
  title = "Add a New Tenant",
  onSubmit = null,
  loadingText = "Adding...",
  initialValues = {},
  isDisabledCompanyDomain = false,
  isDisabledTimezone = false,
}) => {
  const formRef = useRef(null);
  const colors = useColors();
  const queryClient = useQueryClient();
  const { addMSSPTenant, trackEvent } = useAPI();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values) => {
    const newTenant = {
      company_name: values.companyName,
      company_domain: values.companyDomain.replace(/^www\./, ""),
      timezone: values.timezone,
      importance: values.importance,
      labels: values.labels,
      sla: values.sla,
    };

    try {
      await addMSSPTenant(newTenant);
      Toast.success("Tenant created successfully");
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.msspTenants(),
      });
      trackEvent(`User created a new tenant`);
      onClose();
    } catch (error) {
      Toast.error("Failed to create tenant");
    }
  };

  const onSubmitWrapper = async (values) => {
    setIsLoading(true);

    if (onSubmit) {
      await onSubmit(values);
    } else {
      // Use the default handler
      await handleSubmit(values);
    }

    setIsLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ paddingBottom: 0 }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:first-of-type": {
            paddingTop: 0,
          },
        }}
      >
        {/* This Box keeps the sub-titles on top when scrolling down the modal */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: colors.jira_modal_bg,
            zIndex: 1,
            padding: "16px 0",
          }}
        >
          <Typography variant="caption" color={colors.text_secondary}>
            Required fields are marked with an asterisk{" "}
          </Typography>
          <RedAsterisk />
        </Box>

        <AddNewTenantForm
          ref={formRef}
          onSubmit={onSubmitWrapper}
          initialValues={initialValues}
          isDisabledCompanyDomain={isDisabledCompanyDomain}
          isDisabledTimezone={isDisabledTimezone}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
        {/* Styling this button in-place until we have a button-text-inherit style button */}
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            backgroundColor: colors.button_text_inherit_enabled_bg,
            color: colors.button_text_inherit_enabled_text,
            borderRadius: "10px",
            minWidth: "64px",
            padding: "7px 14.5px",
            "&:hover": {
              backgroundColor: colors.button_text_inherit_hovered_bg,
            },
          }}
        >
          <Typography variant="overline">Cancel</Typography>
        </Button>

        {/* Styling this button in-place until we have a button-contained-primary style button */}
        <Button
          variant="contained"
          onClick={() => {
            formRef.current?.submitForm();
          }}
          disabled={isLoading}
          startIcon={isLoading ? <LoadingIcon size={16} /> : null}
          sx={{
            backgroundColor: colors.button_contained_primary_enabled_bg,
            color: colors.button_contained_primary_enabled_text,
            borderRadius: "10px",
            minWidth: "64px",
            padding: "7px 14.5px",
            "&:hover": {
              backgroundColor: colors.button_contained_primary_hovered_bg,
            },
          }}
        >
          <Typography variant="overline">
            {isLoading ? loadingText : "Confirm"}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewTenantModal;
